'use client';

import React, { useState, useEffect, useRef } from 'react';
import styles from './VideoBanner.module.css';
import Video from '../Video';

/**
 * Client Component optimizado que maneja solo la parte interactiva del video
 * - Lazy loading del video cuando está en viewport
 * - Detección de dispositivo móvil
 * - Control de mute/unmute
 */
const VideoBannerClient = ({ urlVideo, urlVideoMobile, posterImage, posterImageMobile }) => {
    const [muted, setMuted] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const [mounted, setMounted] = useState(false);
    const videoContainerRef = useRef(null);

    // Detectar cuando el componente está montado (solo en cliente)
    useEffect(() => {
        setMounted(true);
    }, []);

    // Detectar el tamaño de pantalla
    useEffect(() => {
        if (!mounted) return;

        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, [mounted]);

    // Lazy loading: cargar el video cuando está visible en el viewport
    useEffect(() => {
        if (!videoContainerRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShouldLoadVideo(true);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '50px', // Cargar 50px antes de que sea visible
            }
        );

        observer.observe(videoContainerRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    const mute = () => {
        setMuted(!muted);
    };

    // Usar valores por defecto (desktop) durante SSR y hasta que mounted sea true
    const currentVideoUrl = mounted && isMobile ? urlVideoMobile : urlVideo;
    const currentPoster = mounted && isMobile ? posterImageMobile : posterImage;

    return (
        <div ref={videoContainerRef} className={styles.videoContainer}>
            {shouldLoadVideo ? (
                <>
                    <Video
                        key={currentVideoUrl}
                        link={currentVideoUrl}
                        poster={currentPoster}
                        muted={muted}
                        autoplay={true}
                        loop={true}
                    />
                    {/* Control de volumen - solo visible cuando el video está cargado */}
                    <p className={`btn-toggle_volumen mb-0 ${styles.btnToggleVolumen}`} onClick={mute}>
                        {muted ? <i className="bi bi-volume-mute"></i> : <i className="bi bi-volume-up"></i>}
                    </p>
                </>
            ) : (
                // Mostrar poster mientras no se carga el video
                currentPoster && (
                    <img
                        src={currentPoster}
                        alt="Video banner"
                        className={styles.videoPoster}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                        }}
                    />
                )
            )}
        </div>
    );
};

export default VideoBannerClient;
